import { ReactElement, ReactNode } from 'react';
import dayjs from 'dayjs';
import DatePicker from 'antd/lib/date-picker';
import { Rule } from 'antd/lib/form';
import Input from 'antd/lib/input/Input';
import Select from 'antd/lib/select';

import {
  COUNTRIES_DATA,
  COUNTRIES_SHIP_FROM,
  PAYS_FOR_SHIPPING_DATA,
  PROCESSING_TIME_DATA,
  PRODUCT_SOURCES_DATA,
  RETURN_WINDOW_TIME_DATA,
  SELLING_COUNTRIES_DATA,
} from 'components/Settings/dataConstants';
import {
  formRules,
  momentFormats,
  multipleSelectProps,
  phoneNumberField,
  questionMarks,
} from 'utils/constants/constants';
import { EAccountSupplierFields, EPaymentsFields, EPoliciesFields } from 'types/settings/enums';

export const shippingHeaderFields = [
  { span: 5, title: 'To/Conditions' },
  { span: 5, title: 'Allowed' },
  { span: 7, title: 'Time', questionMark: questionMarks.policies.shippingTime },
  { span: 7, title: 'Price Per Product, $', questionMark: questionMarks.policies.shippingPrice },
];

interface ISettingsFormItemFields {
  label: string;
  name: string;
  component?: any;
  rules?: Rule[];
  componentProps?: any;
  tooltip?: { title: string, icon?: ReactElement };
  options?: {};
  additionalComponent?: ReactNode;
}

export const accountDetailsFields: ISettingsFormItemFields[] = [
  {
    label: 'Contact Full Name',
    name: EAccountSupplierFields.FullName,
    component: Input,
    rules: [formRules.required, formRules.min3Char, formRules.max50Char, formRules.onlyLatinLettersAndSpace],
  },
  {
    label: 'Contact Email',
    name: EAccountSupplierFields.Email,
    component: Input,
    rules: [formRules.required, formRules.email],
  },
  phoneNumberField,
  {
    label: 'Company',
    name: EAccountSupplierFields.CompanyName,
    component: Input,
    rules: [
      formRules.required,
      formRules.min3Char,
      formRules.max25Char,
      {
        type: 'string',
        pattern: /^[A-Za-z0-9&_.\- ]+$/,
        whitespace: true,
        message: 'Can only contain Latin letters and numbers',
      },
    ],
  },
  {
    label: 'Date of Establishment',
    name: EAccountSupplierFields.EstablishmentDate,
    component: DatePicker,
    componentProps: {
      format: momentFormats.default,
      disabledDate: (current: any) => current && current > dayjs().endOf('day'),
    },
    tooltip: { title: questionMarks.account.establishmentDate },
  },
];

export const supplierDescriptionFields: ISettingsFormItemFields[] = [
  {
    label: 'Website',
    name: EAccountSupplierFields.Website,
    component: Input,
    rules: [formRules.required, formRules.webSite],
    componentProps: { placeholder: 'https://www.example.com' },
    tooltip: { title: questionMarks.account.webSite },
  },
  {
    label: 'Products Source',
    name: EAccountSupplierFields.ProductSources,
    component: Select,
    componentProps: multipleSelectProps,
    rules: [formRules.required],
    options: PRODUCT_SOURCES_DATA,
    tooltip: { title: questionMarks.account.productsSource },
  },
  {
    label: 'Where Are Your Products Manufactured?',
    name: EAccountSupplierFields.ManufactureCountries,
    component: Select,
    componentProps: multipleSelectProps,
    options: COUNTRIES_DATA,
  },
  {
    label: 'Amount of SKU’s',
    name: EAccountSupplierFields.SkuAmount,
    component: Input,
    componentProps: { className: 'w-p-100', maxLength: 6 },
    rules: [formRules.onlyNumbers, formRules.whitespace],
    tooltip: { title: questionMarks.account.sku },
  },
  {
    label: 'On What Other Selling Platforms Do You Sell Your Products?',
    name: EAccountSupplierFields.SellingCountries,
    component: Select,
    componentProps: multipleSelectProps,
    rules: [formRules.required],
    options: SELLING_COUNTRIES_DATA,
  },
  {
    label: 'Warehouse Address',
    name: EAccountSupplierFields.WarehouseAddress,
    component: Input,
    rules: [formRules.required, formRules.latinNumberSpecs],
  },
  {
    label: 'VAT Number',
    name: EAccountSupplierFields.VatNumber,
    component: Input,
    rules: [formRules.min5Char, formRules.max20Char, formRules.withoutSpace, formRules.onlyLatinLettersAndNumbers],
  },
];

export const shippingPoliciesFields: ISettingsFormItemFields[] = [
  {
    label: 'What Country Do Your Products Ship From',
    name: EPoliciesFields.ShipFromCountryIds,
    component: Select,
    componentProps: multipleSelectProps,
    rules: [formRules.required],
    options: COUNTRIES_SHIP_FROM,
  },
  {
    label: 'Processing Time',
    name: EPoliciesFields.ProcessingTimeId,
    component: Select,
    options: PROCESSING_TIME_DATA,
    rules: [formRules.required],
    tooltip: { title: questionMarks.policies.processingTime },
  },
];

export const returnPoliciesFields: ISettingsFormItemFields[] = [
  {
    label: 'Who pays for the shipping?',
    name: EPoliciesFields.PaysForShippingId,
    rules: [formRules.required],
    options: PAYS_FOR_SHIPPING_DATA,
    tooltip: { title: questionMarks.policies.paysForShipping },
  },
  {
    label: 'Return window time',
    name: EPoliciesFields.ReturnWindowTimeId,
    rules: [formRules.required],
    options: RETURN_WINDOW_TIME_DATA,
  },
];

export const paymentsRecipientFields: ISettingsFormItemFields[] = [
  {
    label: 'Street and Number',
    name: EPaymentsFields.MainAddress,
    rules: [formRules.required, formRules.whitespace, formRules.latinNumberSpecs],
  },
  {
    label: 'Other Address Info',
    name: EPaymentsFields.AdditionalAddress,
    rules: [formRules.whitespace, formRules.latinNumberSpecs],
    tooltip: { title: questionMarks.payments.otherAddress },
  },
  {
    label: 'City',
    name: EPaymentsFields.City,
    rules: [formRules.required, formRules.whitespace, formRules.latinNumberSpecs],
  },
  {
    label: 'Zip Code',
    name: EPaymentsFields.Zipcode,
    rules: [formRules.required, formRules.whitespace, formRules.latinNumberSpecs],
  },
];

export const paymentsAccountNonUSAFields: ISettingsFormItemFields[] = [
  {
    label: 'SWIFT/BIC',
    name: EPaymentsFields.SwiftBic,
    rules: [formRules.required, { pattern: /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, message: 'Must be valid' }],
    tooltip: { title: questionMarks.payments.swiftBic },
  },
  {
    label: 'IBAN',
    name: EPaymentsFields.Iban,
    rules: [
      formRules.required,
      { min: 15, message: 'Minimum 15 characters' },
      { max: 34, message: 'Maximum 34 characters' },
    ],
    tooltip: { title: questionMarks.payments.iban },
  },
];

export const paymentsAccountUSAFields: ISettingsFormItemFields[] = [
  {
    label: 'Routing Number',
    name: EPaymentsFields.RoutingNumber,
    component: Input,
    rules: [formRules.required, formRules.min5Char, formRules.onlyLatinLettersAndNumbers],
  },
  {
    label: 'Account Holder Name',
    name: EPaymentsFields.AccountHolderName,
    component: Input,
    rules: [formRules.required, formRules.onlyLatinLettersAndSpace, formRules.min3Char, formRules.max50Char],
  },
  {
    label: 'Account Number',
    name: EPaymentsFields.AccountNumber,
    component: Input,
    rules: [formRules.required, formRules.min5Char, formRules.onlyLatinLettersAndNumbers],
  },
  {
    label: 'Account Type',
    name: EPaymentsFields.AccountType,
    component: Select,
    options: ['checking', 'saving'],
    rules: [formRules.required],
  },
];
